import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../common/state/store";

interface IGraphuState {
  isValid: boolean;
  isLoading: boolean;
  shouldRerender: boolean;
  showAllAssets: boolean;
  // One-shot: set only when the user explicitly clicks the Refresh button.
  // loadGraphFct consumes it to force the full reset+spinner ("clean refresh")
  // path; every other (re)load of an already-loaded project stays silent.
  forceFullReload: boolean;
  // True while the silent ("en soumsoum") background refetch is in flight.
  // Unlike isLoading it does NOT blank/replace the graph — it only drives an
  // overlay spinner layered on top of the optimistic graph, so the user sees
  // "something's loading" while the still-mounted graph stays interactive.
  isSilentRefetching: boolean;
}
const initialState: IGraphuState = {
  isValid: false,
  isLoading: false,
  shouldRerender: false,
  showAllAssets: true,
  forceFullReload: false,
  isSilentRefetching: false,
};

export const graphuSlice = createSlice({
  name: "graphu",
  initialState,
  reducers: {
    setIsValid: (state, action: PayloadAction<boolean>) => {
      state.isValid = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsSilentRefetching: (state, action: PayloadAction<boolean>) => {
      state.isSilentRefetching = action.payload;
    },
    setShouldRerender: (state) => {
      state.shouldRerender = !state.shouldRerender;
    },
    requestGraphFullReload: (state) => {
      state.forceFullReload = true;
    },
    clearGraphForceReload: (state) => {
      state.forceFullReload = false;
    },
    setShowAllAssets: (state, action: PayloadAction<boolean>) => {
      state.showAllAssets = action.payload;
    },
    toggleShowAllAssets: (state) => {
      state.showAllAssets = !state.showAllAssets;
    },
  },
});

export const {
  setIsValid,
  setIsLoading,
  setIsSilentRefetching,
  setShouldRerender,
  requestGraphFullReload,
  clearGraphForceReload,
} = graphuSlice.actions;

export const getIsLoading = (state: RootState) => state.graphu.isLoading;
export const getIsSilentRefetching = (state: RootState) => state.graphu.isSilentRefetching;
export const getShouldRerenderGraph = (state: RootState) => state.graphu.shouldRerender;
export const getForceFullReload = (state: RootState) => state.graphu.forceFullReload;
export const getShowAllAssets = (state: RootState) => state.graphu.showAllAssets;

export default graphuSlice.reducer;
